import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getPermissions } from './permisosService';
import { useAuthStore } from './auth-store';

/**
 * @interface PermissionsState
 * @description Define el estado de permisos de secciones y actividades del usuario
 * Los permisos vienen como listas de strings desde el endpoint
 */
export interface PermissionsState {
  /**
   * @property {string[]} sectionPermissions - Lista de secciones permitidas (viene del endpoint)
   * Ejemplo: ["GESTIONAR_TIPO_DOCUMENTO", "GESTIONAR_TIPO_PROVEEDOR", ...]
   */
  sectionPermissions: string[];

  /**
   * @property {string[]} actionPermissions - Lista de actividades/acciones permitidas (viene del endpoint)
   * Ejemplo: ["CREAR_TIPO_DOCUMENTO", "EDITAR_TIPO_DOCUMENTO", "ELIMINAR_TIPO_DOCUMENTO", ...]
   */
  actionPermissions: string[];

  /**
   * @property {boolean} isLoading - Indica si se están cargando los permisos
   */
  isLoading: boolean;

  /**
   * @property {string | null} error - Mensaje de error si falla la carga
   */
  error: string | null;

  /**
   * @function setPermissions
   * @description Establece las listas de permisos de secciones y actividades
   */
  setPermissions: (sections: string[], actions: string[]) => void;

  /**
   * @function hasSectionPermission
   * @description Verifica si una sección está en la lista de permisos
   */
  hasSectionPermission: (section: string) => boolean;

  /**
   * @function hasActionPermission
   * @description Verifica si una actividad está en la lista de permisos
   */
  hasActionPermission: (action: string) => boolean;

  /**
   * @function clearPermissions
   * @description Limpia todos los permisos (útil al hacer logout)
   */
  clearPermissions: () => void;

  /**
   * @function fetchPermissions
   * @description Obtiene las listas de permisos del endpoint
   * TODO: Implementar cuando el endpoint esté listo
   */
  fetchPermissions: () => Promise<void>;
}

/**
 * @constant usePermissionsStore
 * @description Store para gestionar listas de permisos de secciones y actividades
 */
export const usePermissionsStore = create<PermissionsState>()(
  persist(
    (set, get) => ({
      sectionPermissions: [],
      actionPermissions: [],
      isLoading: false,
      error: null,

      setPermissions: (sections: string[], actions: string[]) => {
        set({
          sectionPermissions: sections,
          actionPermissions: actions,
        });
      },

      hasSectionPermission: (section: string): boolean => {
        const { sectionPermissions } = get();
        // Si la lista está vacía, retornar false (o true si quieres permitir por defecto)
        if (sectionPermissions.length === 0) {
          return false;
        }
        // Verificar si la sección está en la lista (case-insensitive)
        return sectionPermissions.some(
          (perm) => perm.toLowerCase().trim() === section.toLowerCase().trim()
        );
      },

      hasActionPermission: (action: string): boolean => {
        const { actionPermissions } = get();
        // Si la lista está vacía, retornar false
        if (actionPermissions.length === 0) {
          return false;
        }
        // Verificar si la actividad está en la lista (case-insensitive)
        return actionPermissions.some(
          (perm) => perm.toLowerCase().trim() === action.toLowerCase().trim()
        );
      },

      clearPermissions: () => {
        set({
          sectionPermissions: [],
          actionPermissions: [],
          error: null,
        });
      },

      fetchPermissions: async () => {
        set({ isLoading: true, error: null });
        try {
          // Obtener el IdUsuario del auth-store
          const idUser = useAuthStore.getState().idUser;

          if (!idUser || idUser <= 0) {
            console.warn('No hay usuario autenticado para obtener permisos');
            set({
              sectionPermissions: [],
              actionPermissions: [],
              isLoading: false,
            });
            return;
          }

          // Obtener los permisos del endpoint
          const permisos = await getPermissions(idUser);

          // Extraer secciones únicas (de ClaveSeccion)
          const seccionesUnicas = new Set<string>();
          permisos.forEach((permiso) => {
            if (permiso.ClaveSeccion && permiso.ClaveSeccion.trim()) {
              seccionesUnicas.add(permiso.ClaveSeccion.trim());
            }
          });

          // Extraer actividades únicas (de todos los ClavesActividad)
          const actividadesUnicas = new Set<string>();
          permisos.forEach((permiso) => {
            if (permiso.ClavesActividad && Array.isArray(permiso.ClavesActividad)) {
              permiso.ClavesActividad.forEach((actividad) => {
                if (actividad && actividad.trim()) {
                  actividadesUnicas.add(actividad.trim());
                }
              });
            }
          });

          // Convertir Sets a Arrays
          const sectionPermissions = Array.from(seccionesUnicas);
          const actionPermissions = Array.from(actividadesUnicas);

          set({
            sectionPermissions,
            actionPermissions,
            isLoading: false,
          });
        } catch (error) {
          console.error('Error fetching permissions:', error);
          set({
            error: 'Error al cargar permisos',
            isLoading: false,
            sectionPermissions: [],
            actionPermissions: [],
          });
        }
      },
    }),
    {
      name: 'permissions-storage',
      partialize: (state) => ({
        sectionPermissions: state.sectionPermissions,
        actionPermissions: state.actionPermissions,
      }),
    }
  )
);

